import React, { useState, useEffect, useRef, useCallback, useMemo } from "react";
import {
  MdDeleteSweep,
  MdOutlineFileDownload,
  MdOutlineVisibility,
  MdOutlineWarning,
  MdSort,
  MdFilterList,
  MdSearch,
  MdClear
} from "react-icons/md";
import {
  BiExport,
  BiAnalyse,
  BiErrorCircle,
  BiCheckCircle,
  BiSortUp,
  BiSortDown
} from "react-icons/bi";
import {
  FaExclamationTriangle,
  FaEye,
  FaRegFilePdf,
  FaRegFileWord,
  FaTimes,
  FaCog,
  FaRobot,
  FaSortAmountDown,
  FaSortAmountUp,
} from 'react-icons/fa';
import { Doughnut } from "react-chartjs-2";
import {
  Button,
  Table,
  Spinner,
  Modal,
  Alert,
  Col,
  Container,
  Card,
  Row,
  Image,
  Badge,
  ProgressBar,
  ButtonGroup,
  Dropdown,
  Tooltip,
  OverlayTrigger,
  Form,
  InputGroup
} from 'react-bootstrap';
import { useTheme } from "../ThemeContext";
import './PastScans.css'
import { ArcElement, Tooltip as ChartTooltip, Legend, Chart as ChartJS } from 'chart.js';
import TemplateSelector from './TemplateSelector';
import TemplateManagement from './TemplateManagement';
import AIReportBuilderModal from './AIReportBuilderModal';

import { exportReport, getAvailableTemplates } from '../services/templateService';
import { useNavigate } from 'react-router-dom';
import { apifetch } from '../services/interceptors';
import GenerateScannerLink from "./LocalhostOnly";
import { Monitor } from 'lucide-react';
import { FaArrowUpRightFromSquare } from "react-icons/fa6";

ChartJS.register(ArcElement, ChartTooltip, Legend);


// Module-level cache to persist across component mounts/unmounts
// This prevents multiple API calls even if the component remounts
let buildYourOwnReportsCache = {
  hasChecked: false,
  isChecking: false, // Legacy, kept for structure
  promise: null, // Shared promise
  licenseType: null,
  isEnabled: null,
  timestamp: null
};

// Cache expiration: 5 minutes (300000 ms)
const CACHE_EXPIRY = 5 * 60 * 1000;

function PastScans({ organizationid }) {
  const [scanResults, setScanResults] = useState([]);
  const [filteredResults, setFilteredResults] = useState([]);
  const [loading, setLoading] = useState(true);
  const [exportloading, setExportloading] = useState(false);
  const [showAIReportBuilder, setShowAIReportBuilder] = useState(false);
  const [error, setError] = useState(null);
  const [exporterror, setExportError] = useState(null);
  const [message, setMessage] = useState(null);
  const [scanning, setScanning] = useState({});
  
  // Sorting and Filtering states
  const [sortConfig, setSortConfig] = useState({
    key: 'created_at',
    direction: 'desc'
  });
  const [filters, setFilters] = useState({
    search: '',
    dateFrom: '',
    dateTo: '',
    minScore: '',
    maxScore: '',
    username: ''
  });
  const [showFilters, setShowFilters] = useState(false);
  const [uniqueUsernames, setUniqueUsernames] = useState([]);
  
  // New modal states
  const [selectExportUrl, setSelectExportUrl] = useState(null);

  // Legacy states (keeping for backward compatibility)
  const [showExportModal, setShowExportModal] = useState(false);
  const [exportFormat, setExportFormat] = useState("pdf");
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [scanIdToDelete, setScanIdToDelete] = useState(null);
  const [reloadScanResult, setReloadScanResult] = useState(false);
  const [actionTotake, setActionTotake] = useState(null);
  const [showActionModal, setShowActionModal] = useState(false);
  const [fileUrl, setFileUrl] = useState(null);
  const [exportTemplate, setExportTemplate] = useState(null);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [viewMode, setViewMode] = useState('table');
  const [selectedScanData, setSelectedScanData] = useState(null);
  const [showTemplateManagement, setShowTemplateManagement] = useState(false);
  const [reportType, setReportType] = useState('detail'); // 'detail' or 'summary'
  const [availableTemplates, setAvailableTemplates] = useState([]); // Store available templates
  const [viewLocalhostScan, setViewLocalhostScan] = useState(false);
  

  // State to track if Build-Your-Own-Reports is enabled
  // Initialize from cache if available, otherwise default to false
  const [isBuildYourOwnReportsEnabled, setIsBuildYourOwnReportsEnabled] = useState(() => {
    const now = Date.now();
    if (buildYourOwnReportsCache.hasChecked &&
      buildYourOwnReportsCache.timestamp &&
      (now - buildYourOwnReportsCache.timestamp) < CACHE_EXPIRY) {
      return buildYourOwnReportsCache.isEnabled === true;
    }
    return false;
  });

  const [userLicenseType, setUserLicenseType] = useState(() => {
    // Check if we have a valid cached license type
    if (buildYourOwnReportsCache.hasChecked &&
      buildYourOwnReportsCache.timestamp &&
      (Date.now() - buildYourOwnReportsCache.timestamp) < CACHE_EXPIRY) {
      return buildYourOwnReportsCache.licenseType || 'community';
    }
    return 'community';
  });

  const chartRef = useRef(null);
  const { isDarkMode } = useTheme();
  const navigate = useNavigate();

  // Helper function to get the current Build-Your-Own-Reports enabled status
  // Always uses cache as source of truth (persists across renders and is updated synchronously)
  const getBuildYourOwnReportsEnabled = useCallback(() => {
    // Check cache first (always up-to-date, updated synchronously)
    if (buildYourOwnReportsCache.hasChecked) {
      return buildYourOwnReportsCache.isEnabled === true;
    }
    // Fallback to state if cache not ready yet
    return isBuildYourOwnReportsEnabled;
  }, [isBuildYourOwnReportsEnabled]);

  const fetchScanResults = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await apifetch('s2/api/scan_results', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({ organizationid })
      });
      if (!response.ok) {
        throw new Error('Failed to fetch scan results');
      }

      const data = await response.json();
      setScanResults(data.result);
      
      // Extract unique usernames for filter dropdown
      const usernames = [...new Set(data.result.map(item => item.username))];
      setUniqueUsernames(usernames);
      
      setLoading(false);
    } catch (error) {
      setError(error.message);
      setLoading(false);
    }
  };

  // Apply sorting and filtering
  useEffect(() => {
    let results = [...scanResults];
    
    // Apply search filter
    if (filters.search) {
      const searchLower = filters.search.toLowerCase();
      results = results.filter(item => 
        item.file_path.split('/').pop().toLowerCase().includes(searchLower) ||
        item.username.toLowerCase().includes(searchLower)
      );
    }
    
    // Apply username filter
    if (filters.username) {
      results = results.filter(item => item.username === filters.username);
    }
    
    // Apply date range filter
    if (filters.dateFrom) {
      const fromDate = new Date(filters.dateFrom).setHours(0, 0, 0, 0);
      results = results.filter(item => new Date(item.created_at) >= fromDate);
    }
    if (filters.dateTo) {
      const toDate = new Date(filters.dateTo).setHours(23, 59, 59, 999);
      results = results.filter(item => new Date(item.created_at) <= toDate);
    }
    
    // Apply score range filter
    if (filters.minScore) {
      results = results.filter(item => parseInt(item.score) >= parseInt(filters.minScore));
    }
    if (filters.maxScore) {
      results = results.filter(item => parseInt(item.score) <= parseInt(filters.maxScore));
    }
    
    // Apply sorting
    results.sort((a, b) => {
      let aValue = a[sortConfig.key];
      let bValue = b[sortConfig.key];
      
      // Handle dates
      if (sortConfig.key === 'created_at') {
        aValue = new Date(aValue).getTime();
        bValue = new Date(bValue).getTime();
      }
      
      // Handle scores as numbers
      if (sortConfig.key === 'score') {
        aValue = parseInt(aValue);
        bValue = parseInt(bValue);
      }
      
      if (aValue < bValue) {
        return sortConfig.direction === 'asc' ? -1 : 1;
      }
      if (aValue > bValue) {
        return sortConfig.direction === 'asc' ? 1 : -1;
      }
      return 0;
    });
    
    setFilteredResults(results);
  }, [scanResults, filters, sortConfig]);

  // Check if Build-Your-Own-Reports module is enabled
  // Uses module-level cache to persist across component mounts/unmounts
  const checkBuildYourOwnReportsEnabled = useCallback(async () => {
    const now = Date.now();

    // 1. If cache is valid, use it immediately
    if (buildYourOwnReportsCache.hasChecked &&
      buildYourOwnReportsCache.timestamp &&
      (now - buildYourOwnReportsCache.timestamp) < CACHE_EXPIRY) {
      const cachedValue = buildYourOwnReportsCache.isEnabled === true;
      console.log('[Build-Your-Own-Reports] Using cached result:', cachedValue);
      setIsBuildYourOwnReportsEnabled(cachedValue);
      setUserLicenseType(buildYourOwnReportsCache.licenseType || 'community');
      return;
    }

    // 2. If a check is already in progress, wait for that specific promise
    if (buildYourOwnReportsCache.promise) {
      console.log('[Build-Your-Own-Reports] Check in progress, subscribing to existing promise...');
      try {
        const result = await buildYourOwnReportsCache.promise;
        setIsBuildYourOwnReportsEnabled(result.isEnabled);
        setUserLicenseType(result.licenseType);
      } catch (e) {
        console.error('[Build-Your-Own-Reports] Existing promise failed, defaulting to false');
        setIsBuildYourOwnReportsEnabled(false);
      }
      return;
    }

    // 3. Start a new check and store the promise
    console.log('[Build-Your-Own-Reports] Starting new check...');

    // Create the promise and assign it to the cache
    buildYourOwnReportsCache.promise = (async () => {
      try {
        const token = localStorage.getItem('token');

        // Step 1: Get user's license type
        console.log('[Build-Your-Own-Reports] Fetching subscription...');
        const subscriptionResponse = await apifetch('/s1/api/subscription', {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`,
          },
        });

        let licenseType = 'community';
        if (subscriptionResponse.ok) {
          const subscriptionData = await subscriptionResponse.json();
          licenseType = subscriptionData.is_pro ? 'pro' : 'community';
        }
        console.log('[Build-Your-Own-Reports] License type:', licenseType);

        // Step 2: Fetch scan-modules
        console.log('[Build-Your-Own-Reports] Fetching modules...');
        const modulesResponse = await apifetch(`/s1/scan-modules?licenseType=${licenseType}`, {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`,
          },
        });

        let isEnabled = false;

        if (modulesResponse.ok) {
          const modulesData = await modulesResponse.json();

          const isBuildYourOwnReportsModule = (moduleName) => {
            if (!moduleName) return false;
            const normalized = moduleName.toLowerCase().replace(/[-\s_]/g, '');
            return normalized.includes('buildyourownreports') ||
              normalized.includes('buildyourownreport') ||
              normalized === 'buildyourownreports';
          };

          // Check new format (licenseTypes array)
          if (modulesData.licenseTypes && Array.isArray(modulesData.licenseTypes)) {
            const license = modulesData.licenseTypes.find(lt => lt.name === licenseType);
            if (license && license.modules) {
              const buildYourOwnModule = license.modules.find(
                m => isBuildYourOwnReportsModule(m.module_name)
              );
              isEnabled = buildYourOwnModule && buildYourOwnModule.module_enabled === true;
            }
          }
          // Check legacy format (object)
          else if (typeof modulesData === 'object' && modulesData !== null) {
            const moduleKey = Object.keys(modulesData).find(key => isBuildYourOwnReportsModule(key));
            if (moduleKey) {
              isEnabled = modulesData[moduleKey] && modulesData[moduleKey].enabled === true;
            }
          }
        }

        console.log('[Build-Your-Own-Reports] Check complete. Enabled:', isEnabled);

        // Update Cache
        buildYourOwnReportsCache.isEnabled = isEnabled;
        buildYourOwnReportsCache.licenseType = licenseType;
        buildYourOwnReportsCache.hasChecked = true;
        buildYourOwnReportsCache.timestamp = Date.now();
        // Keep the promise in cache for a short while or clear it? 
        // Better to clear it so subsequent calls check expiry logic.
        // But we return the result for the current waiter.
        return { isEnabled, licenseType };

      } catch (error) {
        console.error('[Build-Your-Own-Reports] Error during check:', error);
        // Default failure state
        buildYourOwnReportsCache.isEnabled = false;
        buildYourOwnReportsCache.hasChecked = true;
        buildYourOwnReportsCache.timestamp = Date.now();
        return { isEnabled: false, licenseType: 'community' };
      } finally {
        // Clear promise so future calls check expiry
        buildYourOwnReportsCache.promise = null;
      }
    })();

    // Await the newly created promise and update state
    try {
      const result = await buildYourOwnReportsCache.promise;
      setIsBuildYourOwnReportsEnabled(result.isEnabled);
      setUserLicenseType(result.licenseType);
    } catch (e) {
      setIsBuildYourOwnReportsEnabled(false);
    }
  }, []); // Empty dependency array - only runs once on mount

  // Fetch scan results when organization or reload flag changes
  useEffect(() => {
    fetchScanResults();
  }, [organizationid, reloadScanResult]);

  // Check Build-Your-Own-Reports status only once per session
  // Uses module-level cache to persist across component mounts/unmounts
  useEffect(() => {
    // Always call the function - it will check the cache internally
    checkBuildYourOwnReportsEnabled();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Empty array - only run once on mount, never again. Disable exhaustive-deps to prevent re-runs

  // Clear selected template if it's a custom template and feature is disabled
  useEffect(() => {
    // Use the helper function to get the current enabled status
    const isEnabled = getBuildYourOwnReportsEnabled();
    if (!isEnabled && selectedTemplate) {
      const selectedTemplateObj = availableTemplates.find(t => String(t.id) === String(selectedTemplate));
      const isCustom = selectedTemplateObj && (
        selectedTemplateObj.isCustom ||
        String(selectedTemplateObj.id).startsWith('custom_') ||
        selectedTemplateObj.is_default === false
      );
      if (isCustom) {
        // Find first default template or clear selection
        const defaultTemplate = availableTemplates.find(t => t.is_default === true);
        setSelectedTemplate(defaultTemplate ? defaultTemplate.id : null);
      }
    }
  }, [selectedTemplate, availableTemplates, getBuildYourOwnReportsEnabled]);

  const handleDelete = (scanId) => {
    setScanIdToDelete(scanId);
    setShowDeleteModal(true);
  };

  const confirmDelete = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await apifetch(`s2/api/scan_results/${scanIdToDelete}`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        throw new Error('Failed to delete scan');
      }

      setScanResults(scanResults.filter(result => result.scan_id !== scanIdToDelete));
      setShowDeleteModal(false);
      setMessage("Scan deleted successfully");
    } catch (error) {
      setError(error.message);
      setShowDeleteModal(false);
    }
  };

  const cancelDelete = () => {
    setShowDeleteModal(false);
    setShowActionModal(false);
    setShowExportModal(false);
    setExportFormat("");
    setFileUrl(null);
    setExportTemplate(null);
    setSelectedTemplate(null);
    setExportError(null);
    setAvailableTemplates([]);
  };

  const clearFilters = () => {
    setFilters({
      search: '',
      dateFrom: '',
      dateTo: '',
      minScore: '',
      maxScore: '',
      username: ''
    });
  };

  const handleSort = (key) => {
    setSortConfig(prevConfig => ({
      key,
      direction: prevConfig.key === key && prevConfig.direction === 'asc' ? 'desc' : 'asc'
    }));
  };

  const getSortIcon = (key) => {
    if (sortConfig.key !== key) return <MdSort className="ms-1" />;
    return sortConfig.direction === 'asc' ? 
      <BiSortUp className="ms-1" /> : 
      <BiSortDown className="ms-1" />;
  };

  const getScoreCategory = (score) => {
    const numericScore = parseInt(score, 10);
    if (numericScore >= 70) return "#dc3545";
    if (numericScore >= 40) return "#ffc107";
    return "#28a745";
  };

  const getScoreVariant = (score) => {
    const numericScore = parseInt(score, 10);
    if (numericScore >= 70) return "danger";
    if (numericScore >= 40) return "warning";
    return "success";
  };

  const getChartData = (score) => {
    const category = getScoreCategory(score);
    return {
      labels: ["Score", "Remaining"],
      datasets: [
        {
          data: [score, 100 - score],
          backgroundColor: [category, isDarkMode ? "#2a2a2a" : "#f8f9fa"],
          borderWidth: 0,
          borderRadius: 10,
          cutout: '70%'
        }
      ]
    };
  };

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: true,
    plugins: {
      legend: { display: false },
      tooltip: {
        callbacks: {
          label: function (context) {
            return `${context.label}: ${context.raw}%`;
          }
        }
      }
    }
  };

  const handleAnalyse = async (filepath, index) => {
    try {
      setScanning({ [index]: true });
      const token = localStorage.getItem('token');
      const jsontosend = { fileUrl: filepath };
      const response = await apifetch(`s3/api/score/`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify(jsontosend)
      });

      if (!response.ok) {
        throw new Error('Failed to Score');
      }
      setReloadScanResult(prev => !prev);
      setMessage("Analysis completed successfully");
    } catch (error) {
      setError(error.message);
    } finally {
      setScanning({});
    }
  };

  const handleExport = async (filepath, scanData = null) => {
    // Navigate directly to template selection page
    navigate('/report-builder/template-selection', {
      state: {
        scanFilePath: filepath,
        scanData: scanData,
        mode: 'report',
        organizationId: organizationid
      }
    });
  };

  const handleAction = (action) => {
    const parsed_action = JSON.parse(action);
    setActionTotake(parsed_action);
    setShowActionModal(true);
  };

  const handleView = async (fileUrl, index) => {
    try {
      setScanning({ [index]: true });
      const token = localStorage.getItem('token');
      const response = await apifetch('s3/api/read', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({ fileUrl: fileUrl }),
      });

      if (!response.ok) {
        throw new Error('Failed to View the file');
      }
      const data = await response.json();
      const jsonString = JSON.stringify(data['view']);
      const blob = new Blob([jsonString], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const newTab = window.open(url, '_blank');

      newTab.onload = () => {
        URL.revokeObjectURL(url);
      };
    } catch (error) {
      setExportError(error.message);
    } finally {
      setScanning({});
    }
  };

  const sendExportRequest = async (templateIdParam = null) => {
    console.log('[sendExportRequest] templateIdParam:', templateIdParam);
    console.log('[sendExportRequest] selectedTemplate:', selectedTemplate);
    console.log('[sendExportRequest] exportFormat:', exportFormat);
    console.log('[sendExportRequest] reportType:', reportType);

    const templateToUse = templateIdParam !== null ? templateIdParam : selectedTemplate;
    if (!templateToUse) {
      console.error('[sendExportRequest] ERROR - No template selected! templateToUse is:', templateToUse);
      setExportError('Please select a template');
      return;
    }

    // Update selectedTemplate state if a parameter was provided
    if (templateIdParam !== null) {
      setSelectedTemplate(templateIdParam);
    }

    const templateId = String(templateToUse);
    const templateIdNum = parseInt(templateId);

    // Find the selected template in the available templates list
    const selectedTemplateObj = availableTemplates.find(t =>
      String(t.id) === templateId
    );

    // Determine if this is a custom template that should redirect to Report Builder:
    // 1. Template ID starts with "custom_" (ReportBuilder templates from custom_report_templates table)
    // 2. Template has is_default === false (custom templates from report_templates table)
    // 3. Template ID is NOT 1 or 2 (the predefined templates: "Professional Report Template" and "Executive Summary Template")
    // 
    // Only templates with ID 1 or 2 (is_default = true) are predefined and should use backend export
    // All other templates should redirect to Report Builder
    const isPredefinedTemplate = templateIdNum === 1 || templateIdNum === 2;

    // Check if it's a custom template
    let isCustomTemplate = false;
    if (templateId.startsWith('custom_')) {
      // ReportBuilder templates from custom_report_templates table
      isCustomTemplate = true;
    } else if (selectedTemplateObj) {
      // We have the template object, check is_default property
      isCustomTemplate = !selectedTemplateObj.is_default;
    } else if (!isPredefinedTemplate && !isNaN(templateIdNum)) {
      // Fallback: if it's not 1 or 2 and we don't have the template object,
      // assume it's a custom template (safer to redirect than to fail)
      isCustomTemplate = true;
    }

    console.log(`[sendExportRequest] Selected template: ${templateId}`, {
      isCustomTemplate,
      isPredefinedTemplate,
      is_default: selectedTemplateObj?.is_default,
      templateObj: selectedTemplateObj
    });

    if (isCustomTemplate) {
      // CUSTOM TEMPLATE: Redirect to ReportBuilder with scan file and template pre-loaded
      // This way we reuse all the existing widget logic and client-side export
      console.log(`[sendExportRequest] Redirecting to ReportBuilder with custom template...`);

      // Close the export modal
      setShowExportModal(false);

      // Navigate to ReportBuilder with state containing scan file URL and template ID
      navigate('/report-builder', {
        state: {
          fileUrl: selectExportUrl,
          templateId: templateId, // e.g., "custom_123" or numeric ID for custom templates
          fromPastScans: true
        }
      });

      return; // Don't proceed with backend export
    }

    // PREDEFINED TEMPLATE: Use existing backend export
    // Only "Professional Report Template" (id=1) and "Executive Summary Template" (id=2) should use this
    setExportloading(true);
    setFileUrl(null);
    setExportError(null);

    try {
      console.log(`[sendExportRequest] Using predefined template: ${templateId}`);

      // Convert template ID to number for predefined templates
      const templateIdAsNumber = parseInt(templateId);

      const blob = await exportReport(
        selectExportUrl,
        templateIdAsNumber, // Pass as number, not string
        exportFormat,
        reportType
      );

      console.log(`[sendExportRequest] ✅ Predefined template export successful`);

      const objectURL = URL.createObjectURL(blob);
      setFileUrl(objectURL);
      setExportloading(false);
    } catch (error) {
      console.error('[sendExportRequest] Error:', error);
      setExportError(error.message || 'Failed to export the file');
      setExportloading(false);
    }
  };

  const handleDownload = () => {
    if (fileUrl) {
      const fileExtension = exportFormat === 'pdf' ? 'pdf' : 'docx';
      const filename = `scan_result.${fileExtension}`;
      const link = document.createElement('a');
      link.href = fileUrl;
      link.download = filename;
      link.click();
    }
  };

  const handleExportView = () => {
    if (fileUrl) {
      if (exportFormat === 'pdf') {
        window.open(fileUrl, '_blank');
      }
      else {
        const filename = `scan_result.docx`;
        const link = document.createElement('a');
        link.href = fileUrl;
        link.download = filename;
        link.click();
      }
    }
  };

  const handleOpenReportBuilder = () => {
    setShowExportModal(false);
    navigate('/report-builder', {
      state: {
        fileUrl: selectExportUrl,
        fromPastScans: true
      }
    });
  };

  useEffect(() => {
    const timer = setTimeout(() => {
      setError(null);
      setMessage(null);
      setExportError(null);
    }, 4000);

    return () => clearTimeout(timer);
  }, [error, message, exporterror]);

  const renderScoreBadge = (score) => {
    const variant = getScoreVariant(score);
    return (
      <Badge bg={variant} className="score-badge">
        {score}%
      </Badge>
    );
  };

  const renderTableRow = (result, index) => (
    <tr key={result.scan_id} className={isDarkMode ? "table-dark" : ""}>
      <td>{index + 1}</td>
      <td>
        <OverlayTrigger
          placement="top"
          overlay={<Tooltip>{result.file_path.split('/').pop()}</Tooltip>}
        >
          <div className="file-name-container">
            <span className="file-name-text" style={{ fontSize: 12 }}>
              {result.file_path.split('/').pop().substring(0, 15)}<br />
              {result.file_path.split('/').pop().substring(15, 30)}<br />
              {result.file_path.split('/').pop().substring(30, 45)}
            </span>
          </div>
        </OverlayTrigger>
      </td>
      <td className="text-center">
        <small className="">
          {result.username}
        </small>
      </td>
      {/* <td className="text-center">
        <div className="chart-container">
          <Doughnut
            data={getChartData(result.score)}
            options={chartOptions}
            width={80}
            height={80}
          />
          <div className="chart-score">{result.score}%</div>
        </div>
      </td> */}
      <td className="text-center">
        <span className="date-text">
          {new Date(result.created_at).toLocaleDateString()}
        </span>
        <br />
        <small className="">
          {new Date(result.created_at).toLocaleTimeString()}
        </small>
      </td>
      <td className="text-center">
        <OverlayTrigger placement="top" overlay={<Tooltip>View File</Tooltip>}>
          <Button
            variant="outline-primary"
            size="sm"
            onClick={() => handleView(result.file_path, index)}
            disabled={scanning[index]}
          >
            {scanning[index] ? <Spinner animation="border" size="sm" /> : <MdOutlineVisibility />}
          </Button>
        </OverlayTrigger>
      </td>
      <td className="text-center">
        <OverlayTrigger placement="top" overlay={<Tooltip>Analyze</Tooltip>}>
          <Button
            variant="outline-info"
            size="sm"
            onClick={() => handleAnalyse(result.file_path, index)}
            disabled={scanning[index]}
          >
            {scanning[index] ? <Spinner animation="border" size="sm" /> : <BiAnalyse />}
          </Button>
        </OverlayTrigger>
      </td>
      {/* <td className="text-center">
        <OverlayTrigger placement="top" overlay={<Tooltip>View Recommendations</Tooltip>}>
          <Button
            variant="outline-warning"
            size="sm"
            onClick={() => handleAction(result.action)}
          >
            <MdOutlineWarning />
          </Button>
        </OverlayTrigger>
      </td> */}
      <td className="text-center">
        <OverlayTrigger placement="top" overlay={<Tooltip>Generate Report</Tooltip>}>
          <Button
            variant="outline-success"
            size="sm"
            onClick={() => handleExport(result.file_path)}
          >
            <BiExport />
          </Button>
        </OverlayTrigger>
      </td>
      <td className="text-center">
        <OverlayTrigger placement="top" overlay={<Tooltip>Delete</Tooltip>}>
          <Button
            variant="outline-danger"
            size="sm"
            onClick={() => handleDelete(result.scan_id)}
          >
            <MdDeleteSweep />
          </Button>
        </OverlayTrigger>
      </td>
    </tr>
  );

  const renderCardView = () => (
    <Row>
      {filteredResults.map((result, index) => (
        <Col key={result.scan_id} lg={6} xl={4} className="mb-3">
          <Card className={`scan-card h-100 ${isDarkMode ? 'bg-dark text-light border-secondary' : ''}`}>
            <Card.Body>
              <div className="d-flex justify-content-between align-items-start mb-3">
                {/* <Badge bg={getScoreVariant(result.score)}>
                  {result.score}%
                </Badge> */}
                <small style={{ color: isDarkMode ? '#ddd' : '#666' }} className="">
                  {new Date(result.created_at).toLocaleDateString()}
                </small>
              </div>

              <h6 style={{ color: isDarkMode ? '#fff' : '#000' }} className="card-title file-name-text" title={result.file_path.split('/').pop()}>
                {result.file_path.split('/').pop()}
              </h6>
              <small style={{ color: isDarkMode ? '#aaa' : '#666' }} className="d-block mb-2">
                Uploaded by: {result.username}
              </small>

              {/* <div className="chart-container-sm mb-3">
                <Doughnut
                  data={getChartData(result.score)}
                  options={chartOptions}
                  width={60}
                  height={60}
                />
              </div> */}

              <div className="d-flex justify-content-between mt-3">
                <ButtonGroup size="sm">
                  <OverlayTrigger placement="top" overlay={<Tooltip>View</Tooltip>}>
                    <Button
                      variant={isDarkMode ? "outline-light" : "outline-primary"}
                      onClick={() => handleView(result.file_path, index)}
                      disabled={scanning[index]}
                    >
                      {scanning[index] ? <Spinner animation="border" size="sm" /> : <MdOutlineVisibility />}
                    </Button>
                  </OverlayTrigger>

                
                  <OverlayTrigger placement="top" overlay={<Tooltip>Generate Report</Tooltip>}>
                    <Button
                      variant={isDarkMode ? "outline-light" : "outline-success"}
                      onClick={() => handleExport(result.file_path)}
                    >
                      <BiExport />
                    </Button>
                  </OverlayTrigger>

                  <OverlayTrigger placement="top" overlay={<Tooltip>Delete</Tooltip>}>
                    <Button
                      variant={isDarkMode ? "outline-light" : "outline-danger"}
                      onClick={() => handleDelete(result.scan_id)}
                    >
                      <MdDeleteSweep />
                    </Button>
                  </OverlayTrigger>
                </ButtonGroup>
              </div>
            </Card.Body>
          </Card>
        </Col>
      ))}
    </Row>
  );

  const renderFilters = () => (
    <Card className={`mb-4 filter-card ${isDarkMode ? 'bg-dark text-light border-secondary' : ''}`}>
      <Card.Body>
        <div className="d-flex justify-content-between align-items-center mb-3">
          <h6 className="mb-0">
            <MdFilterList className="me-2" />
            Filters
          </h6>
          <Button 
            variant="link" 
            size="sm" 
            onClick={clearFilters}
            className="text-decoration-none"
          >
            <MdClear className="me-1" />
            Clear All
          </Button>
        </div>
        
        <Row>
          <Col md={6} lg={3} className="mb-3">
            <Form.Group>
              <Form.Label>Search</Form.Label>
              <InputGroup>
                <InputGroup.Text className={isDarkMode ? "bg-dark border-secondary text-light" : ""}>
                  <MdSearch />
                </InputGroup.Text>
                <Form.Control
                  type="text"
                  placeholder="File name or uploader..."
                  value={filters.search}
                  onChange={(e) => setFilters({...filters, search: e.target.value})}
                  className={isDarkMode ? "bg-dark text-light border-secondary" : ""}
                />
              </InputGroup>
            </Form.Group>
          </Col>
          
          <Col md={6} lg={3} className="mb-3">
            <Form.Group>
              <Form.Label>Uploader</Form.Label>
              <Form.Select
                value={filters.username}
                onChange={(e) => setFilters({...filters, username: e.target.value})}
                className={isDarkMode ? "bg-dark text-light border-secondary" : ""}
              >
                <option value="">All Uploaders</option>
                {uniqueUsernames.map(username => (
                  <option key={username} value={username}>{username}</option>
                ))}
              </Form.Select>
            </Form.Group>
          </Col>
          
          <Col md={6} lg={3} className="mb-3">
            <Form.Group>
              <Form.Label>Date From</Form.Label>
              <Form.Control
                type="date"
                value={filters.dateFrom}
                onChange={(e) => setFilters({...filters, dateFrom: e.target.value})}
                className={isDarkMode ? "bg-dark text-light border-secondary" : ""}
              />
            </Form.Group>
          </Col>
          
          <Col md={6} lg={3} className="mb-3">
            <Form.Group>
              <Form.Label>Date To</Form.Label>
              <Form.Control
                type="date"
                value={filters.dateTo}
                onChange={(e) => setFilters({...filters, dateTo: e.target.value})}
                className={isDarkMode ? "bg-dark text-light border-secondary" : ""}
              />
            </Form.Group>
          </Col>
          
          {/* <Col md={6} lg={3} className="mb-3">
            <Form.Group>
              <Form.Label>Min Score (%)</Form.Label>
              <Form.Control
                type="number"
                min="0"
                max="100"
                value={filters.minScore}
                onChange={(e) => setFilters({...filters, minScore: e.target.value})}
                className={isDarkMode ? "bg-dark text-light border-secondary" : ""}
              />
            </Form.Group>
          </Col>
          
          <Col md={6} lg={3} className="mb-3">
            <Form.Group>
              <Form.Label>Max Score (%)</Form.Label>
              <Form.Control
                type="number"
                min="0"
                max="100"
                value={filters.maxScore}
                onChange={(e) => setFilters({...filters, maxScore: e.target.value})}
                className={isDarkMode ? "bg-dark text-light border-secondary" : ""}
              />
            </Form.Group>
          </Col> */}
        </Row>
        
        <div className="text-muted small mt-2">
          Showing {filteredResults.length} of {scanResults.length} results
        </div>
      </Card.Body>
    </Card>
  );

  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center py-5">
        <Spinner animation="border" variant="primary" />
        <span className="ms-3">Loading scan results...</span>
      </div>
    );
  }

  return (
    <div className={`past-scans-container ${isDarkMode ? 'dark-mode' : ''}`}>
      {/* Header with view toggle and filter toggle */}
      <div className="d-flex justify-content-between align-items-center mb-3 flex-wrap">
        <h5 className="mb-2 mb-sm-0">Scan Results</h5>
        <div className="d-flex gap-2">
          <Button
            variant={showFilters ? "primary" : "outline-secondary"}
            size="sm"
            onClick={() => setShowFilters(!showFilters)}
            className="d-flex align-items-center"
          >
            <MdFilterList className="me-1" />
            Filters
            {Object.values(filters).some(v => v) && (
              <Badge bg="primary" className="ms-1 rounded-pill">!</Badge>
            )}
          </Button>
          <ButtonGroup size="sm">
            <Button
              variant={viewMode === 'table' ? 'primary' : 'outline-secondary'}
              onClick={() => setViewMode('table')}
            >
              Table View
            </Button>
            <Button
              variant={viewMode === 'card' ? 'primary' : 'outline-secondary'}
              onClick={() => setViewMode('card')}
            >
              Card View
            </Button>
          </ButtonGroup>
        </div>
      </div>

      {/* Filters */}
      {showFilters && renderFilters()}

      {/* Alerts */}
      {error && (
        <Alert variant="danger" dismissible onClose={() => setError(null)} className="mb-3">
          <BiErrorCircle className="me-2" />
          {error}
        </Alert>
      )}
      {message && (
        <Alert variant="success" dismissible onClose={() => setMessage(null)} className="mb-3">
          <BiCheckCircle className="me-2" />
          {message}
        </Alert>
      )}

      {/* Content */}
      {filteredResults.length === 0 ? (
        <div className="text-center py-5">
          <FaExclamationTriangle size={48} className="mb-3 text-warning" />
          <h5>No Scan Results</h5>
          <p className={isDarkMode ? "text-light-50" : "text-muted"}>
            {scanResults.length === 0 
              ? "No scan results available for this organization yet."
              : "No results match your current filters. Try adjusting your filter criteria."}
          </p>
          <p className={isDarkMode ? "text-light-50" : "text-muted"}>You can provision a Localhost Scan to start scanning the <br/> organization's local environment.</p>
          <hr />
          <div className="d-flex justify-content-center mt-3">
  <button
    onClick={() => setViewLocalhostScan((prev) => !prev)}
    className="btn btn-primary d-flex align-items-center gap-2 px-4 py-2"
  >
    <Monitor size={18} />
    <span>Pipsqueak Snapshot</span>
  </button>
</div>
<a
  href="https://pipsqueaknetscan.com/Help-Center"
  target="_blank"
  rel="noopener noreferrer"
  className="link-primary text-decoration-none d-inline-flex align-items-center gap-1"
>
  Learn about Localhost Scans
  <FaArrowUpRightFromSquare size={14} />
</a>
          {scanResults.length > 0 && (
            <Button variant="outline-primary" onClick={clearFilters}>
              Clear Filters
            </Button>
          )}
        </div>
      ) : viewMode === 'table' ? (
        <div className="table-responsive">
          <Table striped bordered hover variant={isDarkMode ? "dark" : ""} className={isDarkMode ? "table-dark" : ""}>
            <thead>
              <tr>
                <th onClick={() => handleSort('index')} style={{ cursor: 'pointer' }}>
                  #
                </th>
                <th onClick={() => handleSort('file_path')} style={{ cursor: 'pointer' }}>
                  File Name {getSortIcon('file_path')}
                </th>
                <th onClick={() => handleSort('username')} style={{ cursor: 'pointer' }} className="text-center">
                  Uploader {getSortIcon('username')}
                </th>
                {/* <th onClick={() => handleSort('score')} style={{ cursor: 'pointer' }} className="text-center">
                  Score {getSortIcon('score')}
                </th> */}
                <th onClick={() => handleSort('created_at')} style={{ cursor: 'pointer' }} className="text-center">
                  Date {getSortIcon('created_at')}
                </th>
                <th className="text-center">View</th>
                <th className="text-center">Analyze</th>
                {/* <th className="text-center">Actions</th> */}
                <th className="text-center">Generate Report</th>
                <th className="text-center">Delete</th>
              </tr>
            </thead>
            <tbody>
              {filteredResults.map((result, index) => renderTableRow(result, index))}
            </tbody>
          </Table>
        </div>
      ) : (
        renderCardView()
      )}
      {filteredResults.length>0 && (
<div
            className="container py-3"
            style={{
              maxWidth: "960px",
              border: "2px dashed #60A5FA",
              borderRadius: "12px",
              background: "#fff",
            }}
          >
            <div className="row align-items-center g-4">
              {/* Column 1 - Icon */}
              <div className="col-auto">
                <div
                  className="d-flex align-items-center justify-content-center rounded-circle"
                  style={{
                    width: "64px",
                    height: "64px",
                    backgroundColor: "#DBEAFE",
                  }}
                >
                  <Monitor size={32} color="#2563EB" />
                </div>
              </div>
      
              {/* Column 2 - Text */}
              <div className="col">
                <h5 className="fw-semibold mb-1">
                  Provision a Localhost Scan
                </h5>
      
                <p className="text-muted mb-0">
                  Run a localhost scan to collect data from the organization's local
                  environment.
                </p>
              </div>
      
              {/* Column 3 - Button */}
              <div className="col-auto">
                <button onClick={() => setViewLocalhostScan((prev) => !prev)} className="btn btn-primary d-flex align-items-center gap-2 px-4 py-2">
                  <Monitor size={18} />
                  <span>Pipsqueak Snapshot</span>
                </button>
              </div>
            </div>
         
      
      </div>
      )}

      <div>
      {viewLocalhostScan && <GenerateScannerLink orgid={organizationid} />}
      </div>
          

      {/* Export Modal */}
      <Modal show={showExportModal} onHide={cancelDelete} centered size="xl" dialogClassName="export-scan-modal">
        <Modal.Header closeButton className={isDarkMode ? "bg-dark text-light border-secondary" : ""}>
          <Modal.Title>
            <BiExport className="me-2" />
            Export Scan Result
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className={isDarkMode ? "bg-dark text-light border-secondary" : ""}>
          {!exportloading && !fileUrl && (
            <>
              {/* Export Format Selection */}
              <div className="mb-4">
                <h6 className="mb-3">1. Select Export Format</h6>
                <ButtonGroup className="w-100">
                  <Button
                    variant={exportFormat === 'pdf' ? 'primary' : isDarkMode ? 'outline-light' : 'outline-primary'}
                    onClick={() => setExportFormat('pdf')}
                    className="d-flex align-items-center justify-content-center"
                  >
                    <FaRegFilePdf className="me-2" />
                    PDF
                  </Button>
                  <Button
                    variant={exportFormat === 'docx' ? 'primary' : isDarkMode ? 'outline-light' : 'outline-primary'}
                    onClick={() => setExportFormat('docx')}
                    className="d-flex align-items-center justify-content-center"
                  >
                    <FaRegFileWord className="me-2" />
                    DOCX
                  </Button>
                </ButtonGroup>
              </div>

              {/* Report Type Selection */}
              <div className="mb-4">
                <h6 className="mb-3">2. Select Report Type</h6>
                <Form.Group>
                  <Form.Check
                    type="radio"
                    id="reportType-detailed"
                    label={
                      <span className={isDarkMode ? "text-light" : ""}>
                        Detailed Report (includes proof tables)
                      </span>
                    }
                    name="reportType"
                    value="detail"
                    checked={reportType === 'detail'}
                    onChange={(e) => setReportType(e.target.value)}
                    className="mb-2"
                  />
                  <Form.Check
                    type="radio"
                    id="reportType-summary"
                    label={
                      <span className={isDarkMode ? "text-light" : ""}>
                        Summary Report (executive overview)
                      </span>
                    }
                    name="reportType"
                    value="summary"
                    checked={reportType === 'summary'}
                    onChange={(e) => setReportType(e.target.value)}
                  />
                </Form.Group>
              </div>

              {/* Template Selector */}
              <div className="mb-3">
                <h6 className="mb-3">3. Select Template</h6>
                <TemplateSelector
                  selectedTemplateId={selectedTemplate}
                  onTemplateSelect={(templateId) => {
                    console.log('[PastScans] Template selected:', templateId);
                    setSelectedTemplate(templateId);
                  }}
                  exportFormat={exportFormat}
                  showManageButton={true}
                  onManageClick={() => {
                    setShowExportModal(false);
                    setShowTemplateManagement(true);
                  }}
                  hideCustomTemplates={!getBuildYourOwnReportsEnabled()}
                  isDarkMode={isDarkMode}
                />
              </div>

              {getBuildYourOwnReportsEnabled() && (
                <div className="mt-4">
                  <h6 className="mb-2">Prefer to design your own report?</h6>
                  <Button
                    variant={isDarkMode ? "outline-light" : "outline-success"}
                    className="w-100 d-flex align-items-center justify-content-center"
                    onClick={handleOpenReportBuilder}
                    disabled={!selectExportUrl}
                  >
                    <FaRegFilePdf className="me-2" />
                    Build a Custom Report Layout
                  </Button>
                  <Button
                    variant={isDarkMode ? "outline-info" : "outline-primary"}
                    className="w-100 d-flex align-items-center justify-content-center mt-2"
                    onClick={() => setShowAIReportBuilder(true)}
                    disabled={!selectExportUrl}
                  >
                    <FaRobot className="me-2" />
                    Build a Reusable Report with AI
                  </Button>
                </div>
              )}
            </>
          )}

          {exportloading && (
            <div className="text-center py-5">
              <Spinner animation="border" variant="primary" size="lg" />
              <h5 className="mt-3">Generating your report...</h5>
              <p className={isDarkMode ? "text-light-50" : "text-muted"}>This may take a moment. Please wait.</p>
            </div>
          )}

          {fileUrl && !exportloading && (
            <div className="export-success text-center">
              <BiCheckCircle size={64} className="text-success mb-3" />
              <h4 className="mb-3">Your report is ready!</h4>
              <div className="d-grid gap-2 col-md-6 mx-auto">
                <Button variant="success" size="lg" onClick={handleDownload}>
                  <MdOutlineFileDownload className="me-2" />
                  Download File
                </Button>
                {exportFormat == 'pdf' &&
                  (<Button variant="outline-primary" onClick={handleExportView}>
                    <MdOutlineVisibility className="me-2" />
                    View in Browser
                  </Button>)}
              </div>
            </div>
          )}

          {exporterror && (
            <Alert variant="danger" className="mt-3">
              <BiErrorCircle className="me-2" />
              <strong>Error:</strong> {exporterror}
            </Alert>
          )}
        </Modal.Body>
        <Modal.Footer className={isDarkMode ? "bg-dark text-light border-secondary" : ""}>
          <Button variant="secondary" onClick={cancelDelete}>
            <FaTimes className="me-2" />
            Cancel
          </Button>
          {!fileUrl && !exportloading && (
            <Button
              variant="primary"
              onClick={() => {
                console.log('[Button Click] selectedTemplate:', selectedTemplate);
                console.log('[Button Click] exportFormat:', exportFormat);
                sendExportRequest();
              }}
              disabled={!selectedTemplate || !exportFormat}
              title={`Generate Report`}
            >
              <BiExport className="me-2" />
              Generate Report
            </Button>
          )}
        </Modal.Footer>
      </Modal>

      <AIReportBuilderModal
        show={showAIReportBuilder}
        onHide={() => setShowAIReportBuilder(false)}
        scanFilePath={selectExportUrl}
      />

      {/* Template Management Modal */}
      <Modal
        show={showTemplateManagement}
        onHide={() => setShowTemplateManagement(false)}
        size="xl"
        fullscreen="lg-down"
        contentClassName={isDarkMode ? "bg-dark text-light" : ""}
      >
        <Modal.Header closeButton className={isDarkMode ? "bg-dark text-light border-secondary" : ""}>
          <Modal.Title>
            <FaCog className="me-2" />
            Template Management
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className={`p-0 ${isDarkMode ? "bg-dark text-light" : ""}`}>
          <TemplateManagement />
        </Modal.Body>
      </Modal>

      {/* Delete Confirmation Modal */}
      <Modal show={showDeleteModal} onHide={cancelDelete} centered contentClassName={isDarkMode ? "bg-dark text-light" : ""}>
        <Modal.Header closeButton className={isDarkMode ? "bg-dark text-light border-secondary" : ""}>
          <Modal.Title>Confirm Deletion</Modal.Title>
        </Modal.Header>
        <Modal.Body className={isDarkMode ? "bg-dark text-light" : ""}>
          <div className="text-center">
            <MdDeleteSweep size={48} className="text-danger mb-3" />
            <p>Are you sure you want to delete this scan result? This action cannot be undone.</p>
          </div>
        </Modal.Body>
        <Modal.Footer className={isDarkMode ? "bg-dark text-light border-secondary" : ""}>
          <Button variant="outline-secondary" onClick={cancelDelete}>
            Cancel
          </Button>
          <Button variant="danger" onClick={confirmDelete}>
            Delete
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Action Modal */}
      <Modal show={showActionModal} onHide={cancelDelete} size="lg" centered contentClassName={isDarkMode ? "bg-dark text-light" : ""}>
        <Modal.Header closeButton className={isDarkMode ? "bg-dark text-light border-secondary" : ""}>
          <Modal.Title>
            <MdOutlineWarning className="me-2" />
            Recommended Actions
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className={isDarkMode ? "bg-dark text-light border-secondary" : ""}>
          <div className="table-responsive">
            <Table striped bordered hover className={isDarkMode ? "table-dark border-secondary" : ""}>
              <thead>
                <tr className={isDarkMode ? "bg-dark text-light border-secondary" : ""}>
                  <th>Category</th>
                  <th>Risk Level</th>
                  <th>Cause</th>
                  <th>Recommendation</th>
                </tr>
              </thead>
              <tbody>
                {actionTotake && Object.keys(actionTotake).map((key, index) => (
                  <tr key={index} className={isDarkMode ? "table-dark" : ""}>
                    <td>
                      <Badge bg="secondary">{key}</Badge>
                    </td>
                    <td>
                      <div className="d-flex align-items-center">
                        <ProgressBar
                          now={actionTotake[key][0]}
                          variant={getScoreVariant(actionTotake[key][0])}
                          className="flex-grow-1 me-2"
                          style={{ height: '8px' }}
                        />
                        <small>{actionTotake[key][0]}%</small>
                      </div>
                    </td>
                    <td>{actionTotake[key][1]}</td>
                    <td>{actionTotake[key][2]}</td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </div>
        </Modal.Body>
        <Modal.Footer className={isDarkMode ? "bg-dark text-light border-secondary" : ""}>
          <Button variant="outline-secondary" onClick={cancelDelete}>
            Close
          </Button>
        </Modal.Footer>
      </Modal>

      <style>
        {`
          .past-scans-container {
            padding: 1rem;
          }
          
          .past-scans-container.dark-mode {
            background-color: #1a1a1a;
            color: #ffffff;
          }
          
          .file-name-container {
            max-width: 100%;
            overflow: hidden;
          }
          
          .file-name-text {
            display: block;
            white-space: nowrap;
            overflow: hidden;
            text-overflow: ellipsis;
            max-width: 100%;
            font-weight: 500;
          }
          
          .chart-container {
            position: relative;
            width: 80px;
            height: 80px;
            margin: 0 auto;
          }
          
          .chart-score {
            position: absolute;
            top: 50%;
            left: 50%;
            transform: translate(-50%, -50%);
            font-weight: bold;
            font-size: 14px;
            color: ${isDarkMode ? '#fff' : '#000'};
          }
          
          .chart-container-sm {
            position: relative;
            width: 60px;
            height: 60px;
            margin: 0 auto;
          }
          
          .scan-card {
            transition: transform 0.2s ease, box-shadow 0.2s ease;
            border: 1px solid ${isDarkMode ? '#444' : '#dee2e6'};
            background-color: ${isDarkMode ? '#2d2d2d' : '#ffffff'};
            color: ${isDarkMode ? '#ffffff' : '#000000'};
          }
          
          .scan-card:hover {
            transform: translateY(-2px);
            box-shadow: 0 4px 12px rgba(0,0,0,0.15);
          }
          
          .filter-card {
            transition: all 0.3s ease;
            border: 1px solid ${isDarkMode ? '#444' : '#dee2e6'};
            background-color: ${isDarkMode ? '#2d2d2d' : '#ffffff'};
            color: ${isDarkMode ? '#ffffff' : '#000000'};
          }
          
          .template-card {
            cursor: pointer;
            transition: all 0.2s ease;
            border: 2px solid transparent;
          }
          
          .template-card:hover {
            border-color: #007bff;
          }
          
          .template-card.selected {
            border-color: #007bff;
            background-color: rgba(0, 123, 255, 0.1);
          }
          
          .template-icon {
            font-size: 24px;
          }
          
          .date-text {
            font-weight: 500;
            color: ${isDarkMode ? '#ddd' : '#333'};
          }
          
          /* Dark mode specific styles */
          .dark-mode .table {
            color: #ffffff;
            background-color: #2d2d2d;
          }
          
          .dark-mode .table thead th {
            background-color: #1a1a1a;
            color: #ffffff;
            border-color: #444;
          }
          
          .dark-mode .table td {
            border-color: #444;
          }
          
          .dark-mode .table-hover tbody tr:hover {
            background-color: #3d3d3d;
            color: #ffffff;
          }
          
          .dark-mode .table-striped tbody tr:nth-of-type(odd) {
            background-color: #2d2d2d;
          }
          
          .dark-mode .table-striped tbody tr:nth-of-type(even) {
            background-color: #333333;
          }
          
          .dark-mode .btn-outline-primary {
            color: #8bb9fe;
            border-color: #8bb9fe;
          }
          
          .dark-mode .btn-outline-primary:hover {
            background-color: #8bb9fe;
            color: #000;
          }
          
          .dark-mode .btn-outline-info {
            color: #6edff6;
            border-color: #6edff6;
          }
          
          .dark-mode .btn-outline-info:hover {
            background-color: #6edff6;
            color: #000;
          }
          
          .dark-mode .btn-outline-success {
            color: #84d88a;
            border-color: #84d88a;
          }
          
          .dark-mode .btn-outline-success:hover {
            background-color: #84d88a;
            color: #000;
          }
          
          .dark-mode .btn-outline-warning {
            color: #ffd966;
            border-color: #ffd966;
          }
          
          .dark-mode .btn-outline-warning:hover {
            background-color: #ffd966;
            color: #000;
          }
          
          .dark-mode .btn-outline-danger {
            color: #f28b82;
            border-color: #f28b82;
          }
          
          .dark-mode .btn-outline-danger:hover {
            background-color: #f28b82;
            color: #000;
          }
          
          .dark-mode .form-control,
          .dark-mode .form-select,
          .dark-mode .input-group-text {
            background-color: #333;
            border-color: #444;
            color: #fff;
          }
          
          .dark-mode .form-control:focus,
          .dark-mode .form-select:focus {
            background-color: #3d3d3d;
            border-color: #666;
            color: #fff;
          }
          
          .dark-mode .form-control::placeholder {
            color: #888;
          }
          
          .dark-mode .text-muted {
            color: #aaa !important;
          }
          
          .dark-mode .modal-content {
            background-color: #2d2d2d;
            color: #fff;
          }
          
          .dark-mode .modal-header,
          .dark-mode .modal-footer {
            border-color: #444;
          }
          
          .dark-mode .close {
            color: #fff;
          }
          
          @media (max-width: 768px) {
            .table-responsive {
              font-size: 14px;
            }
            
            .chart-container {
              width: 60px;
              height: 60px;
            }
            
            .chart-score {
              font-size: 12px;
            }
            
            .file-name-text {
              max-width: 120px;
            }
          }
        `}
      </style>
    </div>
  );
}

export default PastScans;
